import React from 'react';
import { motion } from 'framer-motion';
import { LayoutGrid, Code2, Search, Wrench, DraftingCompass, ServerCog, TrendingUp, LifeBuoy } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Animation Variants (could be moved to a shared file later)
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const ServicesPage = () => {
  const services = [
    {
      icon: DraftingCompass,
      title: "Strategisch Webdesign op Maat",
      keyWord: "Webdesign",
      description: "Meer dan een mooi plaatje. We ontwerpen websites die uw merkverhaal vertellen, uw doelgroep aanspreken en converteren. Focus op gebruiksvriendelijkheid (UX) en esthetiek."
    },
    {
      icon: Code2,
      title: "Moderne Webontwikkeling",
      keyWord: "Webontwikkeling",
      description: "Snelle, veilige en schaalbare websites gebouwd met de nieuwste technologieën (React, Next.js, etc.). Van landingspagina's tot complexe webapplicaties."
    },
    {
      icon: TrendingUp,
      title: "Zoekmachine Optimalisatie (SEO)",
      keyWord: "Optimalisatie (SEO)",
      description: "Verhoog uw online zichtbaarheid. Wij zorgen ervoor dat potentiële klanten uw website vinden via Google door technische SEO, content optimalisatie en linkbuilding."
    },
    {
      icon: LifeBuoy,
      title: "Onderhoud & Technische Support",
      keyWord: "Onderhoud",
      description: "Uw website altijd up-to-date, veilig en performant. Wij bieden onderhoudspakketten en snelle support voor gemoedsrust."
    }
  ];

  // Helper to apply gradient
  const applyGradient = (title, keyWord) => {
    if (!keyWord) return title;
    const parts = title.split(keyWord);
    return (
      <>
        {parts[0]}
        <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">
          {keyWord}
        </span>
        {parts[1]}
      </>
    );
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-16 md:space-y-24"
    >
      <Helmet>
        <title>Webdesign & Ontwikkeling Diensten | SEO & Onderhoud | CylostreamWeb Limburg</title>
        <meta name="description" content="Ontdek onze webdesign diensten in Limburg: maatwerk ontwerp, moderne ontwikkeling, SEO optimalisatie en technisch onderhoud voor uw website." />
        <meta name="keywords" content="webdesign diensten, webontwikkeling Limburg, SEO België, website onderhoud, maatwerk website, CylostreamWeb" />
        <meta property="og:title" content="Webdesign & Ontwikkeling Diensten | SEO & Onderhoud | CylostreamWeb Limburg" />
        <meta property="og:description" content="Ontdek onze webdesign diensten in Limburg: maatwerk ontwerp, moderne ontwikkeling, SEO optimalisatie en technisch onderhoud voor uw website." />
        <link rel="canonical" href="https://www.cylostream.com/services" />
      </Helmet>

      {/* Intro Section */}
      <motion.section variants={fadeInUp} className="text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Onze <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">Diensten</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
          Ontdek hoe CylostreamWeb uw online aanwezigheid kan transformeren met ons uitgebreid aanbod aan weboplossingen, van ontwerp tot onderhoud.
        </p>
      </motion.section>

      {/* Services Grid */}
      <motion.section variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
        {services.map((service, index) => (
          <motion.div 
            key={index} 
            variants={fadeInUp} 
            className="service-detail-card bg-card border rounded-lg p-6 md:p-8 flex flex-col items-start space-y-4"
          >
            <service.icon size={40} className="text-primary mb-2" />
            <h2 className="text-2xl font-semibold">{applyGradient(service.title, service.keyWord)}</h2>
            <p className="text-muted-foreground flex-grow">{service.description}</p>
            {/* Optional: Add a subtle link/button if needed per service */}
            {/* <Button variant="link" className="p-0 h-auto text-primary">Meer info</Button> */}
          </motion.div>
        ))}
      </motion.section>

      {/* CTA Section */}
      <motion.section variants={fadeInUp} className="cta-section text-center bg-slate-50 py-16 rounded-lg">
        <h2 className="text-3xl font-bold mb-4">
          Klaar om uw project te <span className="inline-block bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">starten</span>?
        </h2>
        <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
          Neem contact op voor een vrijblijvend gesprek of vraag direct een offerte aan.
        </p>
        <div className="flex justify-center space-x-4">
          <Button asChild size="lg">
            <Link to="/contact">Contacteer Ons</Link>
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/pricing">Bekijk Prijzen</Link> 
          </Button>
        </div>
      </motion.section>
    </motion.div>
  );
};

export default ServicesPage; 